import SearchIcon from '@mui/icons-material/Search'
import { alpha, Popper, styled } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { Option } from 'components/form-elements/FormSelect'
import { useShowControl } from 'hooks/useShowControl'
import {
  forwardRef,
  HTMLAttributes,
  KeyboardEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { FilterModel } from 'types'

const StyledTextField = styled(TextField)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-root': {
    paddingLeft: `calc(1em + ${theme.spacing(3)}) !important`,
    paddingRight: `6px !important`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '25ch',
    },
  },
}))

const CustomPopper = styled(Popper, {
  shouldForwardProp: (prop) => prop !== 'isColorBackground',
})<{ isColorBackground?: boolean }>(({ isColorBackground }) => ({
  '& .MuiAutocomplete-paper': {
    backgroundColor: isColorBackground ? '#edf4fb' : '#fff',
  },
}))

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.02),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.05),
  },
  width: 'auto',
  flexGrow: 1,
}))

const ListBoxHeader = styled('li')(({ theme }) => ({
  padding: theme.spacing(0, 1, 0, 1),
  fontWeight: 500,
  color: theme.palette.common.black,
  pointerEvents: 'none',
  userSelect: 'none',
  fontSize: '0.875rem',
}))

const StyledOption = styled('li')(() => ({
  minHeight: '36px!important',
}))

export interface SearchInputProps {
  options: Option[] | undefined
  isLoading: boolean
  isColorBackground?: boolean
  getFilters: (value: number) => FilterModel
  onSearch: (searchText: string, filter: FilterModel | null) => void
}

const isOptionEqualToValue = (option: Option, value: Option) => option.value === value.value
const getLowerLabel = (option: Option) => option.label.toString().toLowerCase()

const CustomListBox = forwardRef<HTMLUListElement, HTMLAttributes<HTMLUListElement>>(
  (props, ref) => {
    const { children, ...other } = props
    return (
      <ul {...other} ref={ref}>
        <ListBoxHeader>Search by topic</ListBoxHeader>
        {children}
      </ul>
    )
  },
)
CustomListBox.displayName = 'CustomListBox'

export function useSearchInput({
  options,
  isLoading,
  getFilters,
  onSearch,
  isColorBackground = true,
}: SearchInputProps) {
  const [search, setSearch] = useState<Option | null>(null)
  const [searchInput, setSearchInput] = useState('')

  const [open, onOpen, onClose] = useShowControl(false)

  const onChangeSearchInput = (e: SyntheticEvent<Element, Event>, value: string) => {
    if (!value && (!e || e.type === 'blur')) return
    setSearchInput(value)
    // Reset search to null so that reselecting the same option triggers onChange
    setSearch(null)
  }

  const onChangeSearch = useCallback(
    (e: SyntheticEvent, value: string | Option | null) => {
      if (typeof value === 'string') {
        return
      }

      setSearch(value)
      if (value) {
        onSearch('', getFilters(+value.value))
      }
    },
    [getFilters, onSearch],
  )

  const filterOptions = useCallback((options: Option[], { inputValue }: { inputValue: string }) => {
    if (!inputValue) return []
    const lowerInput = inputValue.toLowerCase()

    const match = options
      .filter((opt) => getLowerLabel(opt).includes(lowerInput))
      .sort(
        (a, b) => getLowerLabel(a).indexOf(lowerInput) - getLowerLabel(b).indexOf(lowerInput),
      )[0]

    if (match) {
      return [match]
    }

    return []
  }, [])

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        onSearch(searchInput, null)
        onClose()
      }
    },
    [onSearch, searchInput, onClose],
  )

  const SearchInput = useMemo(() => {
    return (
      <Search>
        <SearchIconWrapper>
          <SearchIcon />
        </SearchIconWrapper>

        <Autocomplete
          PopperComponent={(props) => (
            <CustomPopper {...props} isColorBackground={isColorBackground} placement="top" />
          )}
          ListboxComponent={CustomListBox}
          popupIcon={null}
          size="small"
          open={open}
          onOpen={onOpen}
          onClose={onClose}
          freeSolo
          value={search}
          isOptionEqualToValue={isOptionEqualToValue}
          onChange={onChangeSearch}
          inputValue={searchInput}
          onInputChange={onChangeSearchInput}
          loading={isLoading}
          options={options || []}
          filterOptions={filterOptions}
          renderOption={(props, option) => (
            <StyledOption {...props} key={String(option.value)}>
              {option.label}
            </StyledOption>
          )}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              placeholder="Enter search terms…"
              InputProps={{
                ...params.InputProps,
                onKeyDown,
              }}
            />
          )}
        />
      </Search>
    )
  }, [
    isLoading,
    onChangeSearch,
    options,
    search,
    searchInput,
    filterOptions,
    onKeyDown,
    open,
    onOpen,
    onClose,
    isColorBackground,
  ])

  return { SearchInput, search, searchInput }
}
